import { motion } from 'framer-motion';
import { 
  Phone, 
  MessageSquare, 
  Mail, 
  Share2, 
  Search, 
  Linkedin, 
  FileText,
  ArrowRight,
  Users,
  Sparkles,
  Cpu
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';

const CALENDLY_URL = "https://calendly.com/romain-trallero/diagnostic-transition-digitale-60min";

const agents = [
  {
    icon: Phone,
    title: 'Standard Téléphonique IA',
    description: 'Répond aux appels entrants 24h/24, qualifie les demandes, prend les rendez-vous, filtre les appels non importants et transfère les urgences.',
    benefit: 'Tu ne rates plus d\'appels lorsque tu es sur tes chantiers.',
    color: 'from-blue-500 to-blue-600',
  },
  {
    icon: MessageSquare,
    title: 'Service Client IA',
    description: 'Répond aux messages WhatsApp, aux messages des réseaux sociaux et aux chats du site internet. Gère les premières demandes clients.',
    benefit: 'Tes clients obtiennent des réponses même lorsque tu travailles.',
    color: 'from-green-500 to-green-600',
  },
  {
    icon: Mail,
    title: 'Gestion des Emails',
    description: 'Analyse les emails entrants, trie les messages, répond automatiquement aux demandes simples, signale les emails importants et crée des tâches.',
    benefit: 'Ta boîte mail ne devient plus un chaos.',
    color: 'from-purple-500 to-purple-600',
  },
  {
    icon: Share2,
    title: 'Marketing Réseaux Sociaux',
    description: 'Création de visuels, rédaction de publications, programmation des posts et interaction avec les commentaires.',
    benefit: 'Ton entreprise reste visible sans que tu y passiez du temps.',
    color: 'from-pink-500 to-pink-600',
  },
  {
    icon: Search,
    title: 'SEO et Blog',
    description: 'Analyse du référencement, rédaction d\'articles optimisés SEO, publication automatique sur le blog et amélioration du positionnement Google.',
    benefit: 'Ton entreprise gagne en visibilité sur internet.',
    color: 'from-orange-500 to-orange-600',
  },
  {
    icon: Linkedin,
    title: 'Prospection LinkedIn',
    description: 'Identification de prospects ciblés, envoi de messages personnalisés, développement du réseau professionnel et suivi des conversations.',
    benefit: 'Créer des opportunités commerciales sans démarchage manuel.',
    color: 'from-indigo-500 to-indigo-600',
  },
  {
    icon: FileText,
    title: 'Assistant Administratif et Juridique',
    description: 'Rédaction de documents, aide administrative, assistance sur les démarches simples et organisation des dossiers.',
    benefit: 'Moins de charge mentale administrative.',
    color: 'from-teal-500 to-teal-600',
  },
];

export default function DigitalTeamPage() {
  return (
    <div data-testid="digital-team-page" className="min-h-screen bg-white">
      <Header />
      
      {/* Hero - Style Fonctionnalités en bleu */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 text-ba-primary text-xs font-semibold uppercase tracking-wider rounded-full mb-6">
              <Cpu className="w-4 h-4" />
              Equipe Digitale
            </span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-800 mb-6 leading-tight">
              Une equipe digitale{' '}
              <span className="text-ba-primary">24h/24 7j/7</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-4 leading-relaxed">
              Une organisation humaine <strong className="text-slate-800">augmentee par l'intelligence artificielle</strong>.
            </p>
            <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-10">
              Ces agents IA sont <strong className="text-slate-800">supervises par ton Super Assistant</strong> qui verifie leur travail et intervient en cas de besoin.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-ba-primary hover:bg-ba-primary/90 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg">
                Decouvrir l'equipe digitale
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* Assistante Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-3xl p-6 md:p-12 border border-ba-primary/10"
          >
            <div className="flex flex-col md:flex-row md:items-start gap-4 md:gap-6 mb-6">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-ba-primary/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Users className="w-7 h-7 md:w-8 md:h-8 text-ba-primary" />
              </div>
              <div className="w-full">
                <h2 className="text-xl md:text-4xl font-bold text-slate-800 mb-4">
                  Ton assistant : le chef d'orchestre de ton organisation
                </h2>
                <p className="text-slate-600 text-base md:text-lg leading-relaxed">
                  Chaque client dispose d'une assistante humaine dédiée disponible 40 heures par semaine. 
                  Elle comprend le fonctionnement de ton entreprise, récupère les informations importantes, 
                  organise les tâches, pilote les agents IA, vérifie le travail automatisé et communique directement avec toi.
                </p>
              </div>
            </div>
            <p className="text-slate-600 text-base md:text-lg">
              Elle transforme un ensemble d'outils technologiques en organisation concrète et efficace.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Agents Grid */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-lg text-slate-600 mb-4">
              Découvre les assistants digitaux qui composent ton équipe.
            </p>
            <h2 className="text-3xl md:text-5xl font-bold text-slate-800">
              Les membres de ton équipe digitale
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {agents.map((agent, index) => {
              const Icon = agent.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 border border-slate-100 hover:shadow-xl transition-all group"
                >
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${agent.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-rm-text mb-3">{agent.title}</h3>
                  <p className="text-rm-text-secondary text-sm mb-4 leading-relaxed">
                    {agent.description}
                  </p>
                  <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                    <p className="text-green-700 text-sm font-medium">
                      ✓ {agent.benefit}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Result Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Sparkles className="w-16 h-16 text-ba-primary mx-auto mb-6" />
            <h2 className="text-3xl md:text-5xl font-bold text-slate-800 mb-6">
              Tu n'es plus seul
            </h2>
            <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
              Grâce à cette organisation, tu passes d'un entrepreneur qui doit tout gérer 
              à une entreprise organisée avec une équipe complète qui travaille pour toi 24h/24. 
              Tu peux enfin te concentrer sur ton métier, tes clients, ta croissance 
              et ta vie personnelle.
            </p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
              <Button className="bg-ba-primary hover:bg-ba-primary/90 text-white font-semibold rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all group">
                Réserver un entretien avec Romain
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
